import { Component, signal } from '@angular/core';

import { BehaviorSubject, of } from 'rxjs';

import { FormComponent } from '@camelot/form-basic';
import {
  ColorPickerComponent,
  DatePickerComponent,
  DropdownComponent,
  InputChoicesComponent,
  InputPhoneComponent,
  SliderComponent,
  SwitchComponent,
  TimePickerComponent,
  VatNumberComponent,
} from '@camelot/form-input';
import { InputChoices, InputColorPicker, InputDatePicker, InputDropdown, InputDynamic, CamAbstractInput, InputPhone, InputSlider, InputSwitch, InputTimePicker, InputTranslation, InputVatNumber } from '@camelot/form-model';

import { CamContactService } from '@camelot/services';

import { TestIdDirective } from '@camelot/testing-harness';

export const advancedSpy = {
  searchCalls: signal(0),
  vatCalls: signal(0),
};

const contactServiceMock = {
  validateVatNumber$: () => {
    advancedSpy.vatCalls.update(count => count + 1);
    return of({ isValid: true });
  },
};

type SwitchMatch =
  | { prop: unknown; type: 'checkbox' | 'datePicker' | 'number' | 'textbox' }
  | { prop: never; type: 'dropdown' };

@Component({
    imports: [
        ColorPickerComponent,
        DatePickerComponent,
        DropdownComponent,
        FormComponent,
        InputChoicesComponent,
        InputPhoneComponent,
        SliderComponent,
        SwitchComponent,
        TestIdDirective,
        TimePickerComponent,
        VatNumberComponent,
    ],
    providers: [{ provide: CamContactService, useValue: contactServiceMock }],
    template: `
    <cam-input-date-picker camTestId="fa-date" [input]="this.date" [standalone]="true" />
    <div camTestId="fa-date-value">{{ this.dateValue() }}</div>

    <cam-input-date-picker camTestId="fa-range" [input]="this.range" [standalone]="true" />
    <div camTestId="fa-range-value">{{ this.rangeValue() }}</div>

    <cam-input-time-picker camTestId="fa-time" [input]="this.time" [standalone]="true" />
    <div camTestId="fa-time-value">{{ this.timeValue() }}</div>

    <cam-input-slider camTestId="fa-slider" [input]="this.slider" [standalone]="true" />
    <div camTestId="fa-slider-value">{{ this.sliderValue() }}</div>

    <cam-input-color-picker camTestId="fa-color" [input]="this.color" [standalone]="true" />
    <div camTestId="fa-color-value">{{ this.colorValue() }}</div>

    <cam-input-dropdown camTestId="fa-multi-dropdown" [input]="this.multiDropdown" [standalone]="true" />
    <div camTestId="fa-multi-dropdown-value">{{ this.multiDropdownValue() }}</div>

    <cam-input-choices camTestId="fa-choices" [input]="this.choices" [standalone]="true" />
    <div camTestId="fa-choices-search-calls">{{ this.searchCalls() }}</div>

    <cam-input-vat-number camTestId="fa-vat" [input]="this.vat" [standalone]="true" />
    <div camTestId="fa-vat-calls">{{ this.vatCalls() }}</div>

    <cam-input-phone camTestId="fa-phone" [input]="this.phone" [standalone]="true" />
    <div camTestId="fa-phone-value">{{ this.phoneValue() }}</div>

    <cam-input-switch camTestId="fa-switch" [input]="this.switch" [standalone]="true" />
    <button camTestId="fa-switch-to-checkbox" (click)="this.switchToCheckbox()">to checkbox</button>

    <cam-form camTestId="fa-dynamic-form" [inputs]="this.dynamicInputs" />
    <cam-form camTestId="fa-translation-form" [inputs]="this.translationInputs" />
  `
})
export class FormAdvancedCase {
  public choices = new InputChoices({
    advancedSearch$: () => {
      advancedSpy.searchCalls.update(count => count + 1);
      return of([{ data: null, id: '1', name: 'Search result one' }]);
    },
    key: 'choices',
    label: 'Choices',
    withSearch: true,
  });
  public color = new InputColorPicker({ key: 'color', label: 'Color' });
  public colorValue = signal('');
  public date = new InputDatePicker({ key: 'date', label: 'Date' });
  public dateValue = signal('');
  public range = new InputDatePicker({ key: 'range', label: 'Range', rangeEnabled: true });
  public rangeValue = signal('');
  public time = new InputTimePicker({ key: 'time', label: 'Time' });
  public timeValue = signal('');
  public dynamicInputs: CamAbstractInput[] = [
    new InputDynamic({
      enableDelete: true,
      key: 'dynamic',
      label: 'Dynamic',
      template: [{ options: { key: 'name', label: 'Line name' }, type: 'InputTextBox' }],
    }),
  ];
  public multiDropdown = new InputDropdown<string>({
    key: 'multi',
    label: 'Multi dropdown',
    multiple: true,
    options: of([
      { id: 'a', name: 'Multi A' },
      { id: 'b', name: 'Multi B' },
    ]),
    withSearch: true,
  });
  public multiDropdownValue = signal('');
  public phone = new InputPhone({ key: 'phone', label: 'Phone' });
  public phoneValue = signal('');
  public searchCalls = advancedSpy.searchCalls;
  public slider = new InputSlider({ key: 'slider', label: 'Slider', max: 10, min: 0 });
  public sliderValue = signal('');
  public switch = new InputSwitch({
    key: 'switch',
    label: 'Switch',
    match: this.createMatch$(),
  });
  public translationInputs: CamAbstractInput[] = [
    new InputTranslation({
      key: 'translation',
      label: 'Translation',
      template: [{ options: { key: 'text', label: 'Translated text' }, type: 'InputTextBox' }],
    }),
  ];
  public vat = new InputVatNumber({ key: 'vat', label: 'VAT' });
  public vatCalls = advancedSpy.vatCalls;

  private _match$!: BehaviorSubject<SwitchMatch>;

  ngOnInit(): void {
    this.date.changeValue$.subscribe(value => this.dateValue.set(JSON.stringify(value)));
    this.range.changeValue$.subscribe(value => this.rangeValue.set(JSON.stringify(value)));
    this.time.changeValue$.subscribe(value => this.timeValue.set(JSON.stringify(value)));
    this.slider.changeValue$.subscribe(value => this.sliderValue.set(String(value ?? '')));
    this.color.changeValue$.subscribe(value => this.colorValue.set(String(value ?? '')));
    this.multiDropdown.changeValue$.subscribe(value => this.multiDropdownValue.set(JSON.stringify(value)));
    this.phone.changeValue$.subscribe(value => this.phoneValue.set(String(value ?? '')));
  }

  public switchToCheckbox(): void {
    this._match$.next({ prop: {}, type: 'checkbox' });
  }

  private createMatch$(): BehaviorSubject<SwitchMatch> {
    this._match$ = new BehaviorSubject<SwitchMatch>({ prop: {}, type: 'textbox' });
    return this._match$;
  }
}
